/**
 * Component-level theme resolution for the interactive components.
 *
 * Port of the web widget's `lib/theme-utils.ts`, with the same deliberate
 * quirk preserved: component-level radii (4px/8px) differ from widget-level
 * radii (6px/12px). That is not a bug in the source — the interactive cards
 * are visually tighter than the widget chrome — so it survives the port.
 *
 * Everything is resolved to NUMBERS here. The web version returns CSS strings
 * (`"8px"`, `"8px 16px"`) because it hands them to the DOM; RN needs numeric
 * style values, and a padding shorthand has to become explicit vertical and
 * horizontal components.
 */

import type { ButtonSize, ButtonStyle, ComponentTheme } from "@ringg/core";

/** Vertical/horizontal padding pair — RN has no shorthand string. */
export interface ButtonPadding {
  paddingVertical: number;
  paddingHorizontal: number;
}

export const DEFAULT_THEME: Required<ComponentTheme> = {
  primaryColor: "#0a0a0b",
  primaryTextColor: "#ffffff",
  backgroundColor: "#ffffff",
  surfaceColor: "#f3f4f6",
  textColor: "#0a0a0b",
  mutedTextColor: "#6b7280",
  borderColor: "#e5e7eb",
  errorColor: "#ef4444",
  successColor: "#16a34a",
  fontFamily: "inherit",
  fontSize: "14px",
  borderRadius: "8px",
  padding: "12px",
  buttonStyle: "pill",
  buttonSize: "md",
};

/** Matches `resolveRadius`'s pill clamp — RN cannot lay out an infinite radius. */
const PILL_RADIUS = 9999;

export const mergeTheme = (theme?: ComponentTheme): Required<ComponentTheme> => ({ ...DEFAULT_THEME, ...theme });

/** `"14px"` → `14`, for the string-typed `fontSize` / `padding` theme fields. */
export const toNumber = (value: string | undefined, fallback: number): number => {
  if (value === undefined) return fallback;
  const trimmed = value.trim();
  const parsed = Number(trimmed.endsWith("px") ? trimmed.slice(0, -2) : trimmed);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getButtonRadius = (style?: ButtonStyle): number => {
  switch (style) {
    case "pill":
      return PILL_RADIUS;
    case "square":
      return 4;
    case "rounded":
    default:
      return 8;
  }
};

export const getButtonPadding = (size?: ButtonSize): ButtonPadding => {
  switch (size) {
    case "sm":
      return { paddingVertical: 6, paddingHorizontal: 12 };
    case "lg":
      return { paddingVertical: 12, paddingHorizontal: 24 };
    case "md":
    default:
      return { paddingVertical: 8, paddingHorizontal: 16 };
  }
};

export const getButtonHeight = (size?: ButtonSize): number => {
  switch (size) {
    case "sm":
      return 32;
    case "lg":
      return 44;
    case "md":
    default:
      return 36;
  }
};
